import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import OnlineMembersPanel from '../../components/common/OnlineMembersPanel';
import ChatPanel from '../../components/common/ChatPanel';

export default function MemberMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [selectedMsg, setSelectedMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ subject: '', message: '' });
  const [attachment, setAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);
  const [showOnlinePanel, setShowOnlinePanel] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null); // null = to admin, object = to member
  const [chatUser, setChatUser] = useState(null); // when set, shows the chat panel

  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get('/messages/my/');
      setMessages(res.data.results || res.data);
    } catch {
      toast.error('Failed to load messages.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB.');
      return;
    }
    setAttachment(file);
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
  };

  const clearAttachment = () => {
    setAttachment(null);
    setPreviewUrl(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Subject and message are required.');
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('subject', formData.subject);
      data.append('message', formData.message);
      if (attachment) data.append('attachment', attachment);

      if (selectedRecipient) {
        // Sending to another member
        data.append('recipient_id', selectedRecipient.id);
        await api.post('/messages/send-to-member/', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(`Message sent to ${selectedRecipient.name}!`);
      } else {
        // Sending to admin
        await api.post('/messages/my/', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Message sent to admin!');
      }
      setShowCompose(false);
      setFormData({ subject: '', message: '' });
      setSelectedRecipient(null);
      clearAttachment();
      fetchMessages();
    } catch {
      toast.error('Failed to send message.');
    } finally {
      setSubmitting(false);
    }
  };

  const markRead = async (msg) => {
    if ((msg.source === 'admin' || (msg.source === 'peer' && msg.is_incoming)) && !msg.is_read) {
      try {
        await api.patch(`/messages/my/${msg.id}/read/`);
        setMessages((prev) => prev.map((m) => m.id === msg.id ? { ...m, is_read: true } : m));
      } catch { /* ignore */ }
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const getFileIcon = (url) => {
    if (!url) return null;
    const ext = url.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
    if (['pdf'].includes(ext)) return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx'].includes(ext)) return '📊';
    return '📎';
  };

  return (
    <div className="flex gap-6">
      {/* Main Messages Area */}
      <div className="flex-1 min-w-0">

      {/* Chat Panel - shown when chatting with a member */}
      {chatUser ? (
        <div className="flex flex-col" style={{ height: 'calc(100vh - 7rem)' }}>
          <div className="flex items-center gap-3 mb-3 flex-shrink-0">
            <button onClick={() => setChatUser(null)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
              <p className="text-sm text-gray-500 mt-0.5">Chatting with {chatUser.name}</p>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <ChatPanel chatUser={chatUser} onClose={() => setChatUser(null)} currentUserRole="member" />
          </div>
        </div>
      ) : (
      <>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
          <p className="text-sm text-gray-500 mt-1">Send and track messages</p>
        </div>
        <button
          onClick={() => { setShowCompose(!showCompose); setSelectedMsg(null); setSelectedRecipient(null); }}
          className="bg-[#0B5D1E] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition flex items-center gap-2"
        >
          {showCompose ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              New Message
            </>
          )}
        </button>
      </div>

      {/* Compose Form */}
      {showCompose && (
        <form onSubmit={handleSend} className="bg-white rounded-xl shadow-sm border mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-[#062E10] to-[#0B5D1E] px-6 py-4">
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {selectedRecipient ? `Message to ${selectedRecipient.name}` : 'Message to Admin'}
            </h3>
          </div>
          <div className="p-6 space-y-4">
            {/* Recipient indicator */}
            {selectedRecipient && (
              <div className="flex items-center justify-between bg-[#0B5D1E]/5 rounded-lg px-4 py-3 border border-[#0B5D1E]/20">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#0B5D1E]/10 text-[#0B5D1E] flex items-center justify-center text-sm font-bold">
                    {selectedRecipient.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{selectedRecipient.name}</p>
                    {selectedRecipient.membership_number && (
                      <p className="text-xs text-gray-500">NIS/ {selectedRecipient.membership_number}</p>
                    )}
                  </div>
                </div>
                <button type="button" onClick={() => setSelectedRecipient(null)}
                  className="text-xs text-gray-500 hover:text-red-500 transition px-2 py-1 rounded hover:bg-red-50">
                  Send to Admin instead
                </button>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Subject</label>
              <input type="text" required value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Enter message subject..."
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Message</label>
              <textarea required value={formData.message} rows={5}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder={selectedRecipient ? `Write your message to ${selectedRecipient.name}...` : 'Write your message to the admin...'}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none resize-none" />
            </div>

            {/* Attachment */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Attachment (optional)</label>
              <div className="flex items-start gap-4">
                <label className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#0B5D1E] hover:bg-[#0B5D1E]/5 transition">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  <span className="text-sm text-gray-500">{attachment ? 'Change file' : 'Attach file'}</span>
                  <input ref={fileRef} type="file" className="hidden" onChange={handleFileChange}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
                </label>
                {attachment && (
                  <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-2.5 border">
                    {previewUrl ? (
                      <img src={previewUrl} alt="Preview" className="w-12 h-12 rounded object-cover" />
                    ) : (
                      <span className="text-2xl">{getFileIcon(attachment.name)}</span>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate max-w-[200px]">{attachment.name}</p>
                      <p className="text-xs text-gray-400">{(attachment.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button type="button" onClick={clearAttachment}
                      className="text-red-400 hover:text-red-600 p-1 flex-shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">Max 10MB. Accepts images, PDF, Word, Excel, text files.</p>
            </div>
          </div>
          <div className="px-6 pb-6">
            <button type="submit" disabled={submitting}
              className="bg-[#0B5D1E] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#062E10] transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
              Send Message
            </button>
          </div>
        </form>
      )}

      {/* Messages List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#0B5D1E]/20 border-t-[#0B5D1E] rounded-full animate-spin" />
        </div>
      ) : messages.length === 0 && !showCompose ? (
        <div className="bg-white rounded-xl shadow-sm border text-center py-16">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-bold text-gray-900 mb-1">No Messages Yet</h3>
          <p className="text-gray-500 text-sm mb-4">Start a conversation with the admin.</p>
          <button onClick={() => setShowCompose(true)}
            className="bg-[#0B5D1E] text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition inline-flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            Send Your First Message
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Message List Panel */}
          <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {messages.map((msg) => (
              <button key={msg.id} onClick={() => { setSelectedMsg(msg); setShowCompose(false); markRead(msg); }}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  selectedMsg?.id === msg.id
                    ? 'border-[#0B5D1E] bg-[#0B5D1E]/5 shadow-sm'
                    : ((msg.source === 'admin' || (msg.source === 'peer' && msg.is_incoming)) && !msg.is_read)
                      ? 'border-[#C8A951]/30 bg-[#FFFDF5] hover:bg-[#FFF9E6]'
                      : 'border-gray-100 bg-white hover:bg-gray-50 hover:border-gray-200'
                }`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {(msg.source === 'admin' || (msg.source === 'peer' && msg.is_incoming)) && !msg.is_read && <span className="w-2 h-2 rounded-full bg-[#C8A951] flex-shrink-0" />}
                      {msg.source === 'admin' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#C8A951]/20 text-[#8B6914] font-semibold flex-shrink-0">From Admin</span>
                      )}
                      {msg.source === 'peer' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold flex-shrink-0">
                          {msg.is_incoming ? `From ${msg.sender_name || 'Member'}` : `To ${msg.recipient_name || 'Member'}`}
                        </span>
                      )}
                    </div>
                    <p className="font-semibold text-sm text-gray-900 truncate mt-0.5">{msg.subject}</p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{msg.message}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {msg.source === 'admin' ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-[#C8A951]/20 text-[#8B6914]">Received</span>
                    ) : msg.source === 'peer' ? (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${msg.is_incoming ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {msg.is_incoming ? 'Received' : 'Sent'}
                      </span>
                    ) : msg.admin_reply ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-green-100 text-green-700">Replied</span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-yellow-100 text-yellow-700">Pending</span>
                    )}
                    {msg.attachment && (
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">{formatDate(msg.created_at)}</p>
              </button>
            ))}
          </div>

          {/* Message Detail Panel */}
          <div className="lg:col-span-2">
            {selectedMsg ? (
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                {/* Detail Header */}
                <div className="bg-[#F8FAFB] px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-gray-900">{selectedMsg.subject}</h2>
                    {selectedMsg.source === 'admin' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-[#C8A951]/20 text-[#8B6914]">From Admin</span>
                    )}
                    {selectedMsg.source === 'peer' && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-blue-100 text-blue-700">
                        {selectedMsg.is_incoming ? `From ${selectedMsg.sender_name}` : `To ${selectedMsg.recipient_name || 'Member'}`}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {selectedMsg.source === 'admin' || (selectedMsg.source === 'peer' && selectedMsg.is_incoming) ? 'Received' : 'Sent'} {formatDate(selectedMsg.created_at)}
                  </p>
                </div>

                {/* Your Message */}
                <div className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      selectedMsg.source === 'admin' ? 'bg-[#C8A951] text-[#062E10]' : selectedMsg.source === 'peer' && selectedMsg.is_incoming ? 'bg-blue-500 text-white' : 'bg-[#0B5D1E] text-white'
                    }`}>
                      {selectedMsg.source === 'admin' ? 'A' : selectedMsg.source === 'peer' && selectedMsg.is_incoming ? (selectedMsg.sender_name || 'M').charAt(0).toUpperCase() : 'You'}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900 mb-1">
                        {selectedMsg.source === 'admin' ? 'Admin Message' : selectedMsg.source === 'peer' && selectedMsg.is_incoming ? (selectedMsg.sender_name || 'Member Message') : selectedMsg.source === 'peer' ? `Your message to ${selectedMsg.recipient_name || 'Member'}` : 'Your Message'}
                      </p>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-gray-50 rounded-lg p-4">
                        {selectedMsg.message}
                      </div>
                    </div>
                  </div>

                  {/* Attachment */}
                  {selectedMsg.attachment && (
                    <div className="ml-11 mb-6">
                      <p className="text-xs font-medium text-gray-500 mb-2">Attachment</p>
                      <a href={selectedMsg.attachment} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 border rounded-lg hover:bg-gray-100 transition text-sm text-[#0B5D1E] font-medium">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        View Attachment
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  )}

                  {/* Admin Reply (only for member-sent messages) */}
                  {selectedMsg.source === 'member' && (
                    selectedMsg.admin_reply ? (
                      <div className="flex items-start gap-3 mt-6 pt-6 border-t border-gray-100">
                        <div className="w-8 h-8 rounded-full bg-[#C8A951] flex items-center justify-center text-[#062E10] text-xs font-bold flex-shrink-0">
                          ADM
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-semibold text-gray-900">Admin Reply</p>
                            <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                              {formatDate(selectedMsg.replied_at)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed bg-[#FFFDF5] rounded-lg p-4 border border-[#C8A951]/20">
                            {selectedMsg.admin_reply}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-700 rounded-full text-sm font-medium">
                          <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" opacity="0.2" />
                            <circle cx="12" cy="12" r="4" />
                          </svg>
                          Awaiting admin response
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">Select a message to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
      </>
      )}
      </div>

      {/* Online Members Panel - Right Sidebar (desktop) */}
      <div className="hidden lg:block w-72 flex-shrink-0">
        <div className="sticky top-24">
          <OnlineMembersPanel
            currentUserRole="member"
            activeChatUserId={chatUser?.id}
            onSelectMember={(member) => {
              setChatUser(member);
              setShowCompose(false);
              setSelectedMsg(null);
            }}
          />
        </div>
      </div>

      {/* Online Members Panel - Mobile/Tablet toggle button */}
      <button
        onClick={() => setShowOnlinePanel(!showOnlinePanel)}
        className="lg:hidden fixed bottom-6 right-6 z-40 bg-[#0B5D1E] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#062E10] transition-all"
        title="Online Members"
      >
        <div className="relative">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-green-400 rounded-full border-2 border-[#0B5D1E] animate-pulse" />
        </div>
      </button>

      {/* Online Members Panel - Mobile/Tablet slide-over */}
      {showOnlinePanel && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setShowOnlinePanel(false)} />
          <div className="lg:hidden fixed right-0 top-0 h-full w-80 max-w-[85vw] bg-[#F8FAFB] z-50 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
              <h3 className="text-sm font-bold text-gray-900">Online Members</h3>
              <button onClick={() => setShowOnlinePanel(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-3">
              <OnlineMembersPanel
                currentUserRole="member"
                activeChatUserId={chatUser?.id}
                onSelectMember={(member) => {
                  setShowOnlinePanel(false);
                  setChatUser(member);
                  setShowCompose(false);
                  setSelectedMsg(null);
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
